import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useI18n } from "@/i18n";

export default function Terms() {
  const { lang } = useI18n();
  const isEs = lang === "es";

  return (
    <div className="min-h-screen bg-obsidian text-gold-light">
      <header className="border-b border-gold-subtle bg-obsidian-2/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="terms-back-home">
            <Logo />
          </Link>
          <Link to="/" className="text-xs text-faded hover:text-gold">
            ← Volver
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl mb-2">Términos y Condiciones</h1>
        <p className="text-xs text-faded mb-12">
          Última actualización: 25 de mayo de 2026 · v1.0-2026-05-25
        </p>

        {!isEs && <LegalLangNotice lang={lang} />}

        {isEs && (
          <div className="space-y-8 text-sm leading-relaxed">
            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                1. Objeto y descripción del servicio
              </h2>
              <p className="mb-3">
                KeepSoul es un servicio digital prestado por Antonio Ferrer
                García
                {/* PRIVACY-NIF-HIDDEN — restaurar NIF real o sustituir por CIF de SL antes de lanzamiento público */}
                {/* PRIVACY-ADDRESS-HIDDEN — restaurar o sustituir por domicilio profesional antes de lanzamiento público */}
                {" "}(identificación fiscal y dirección postal disponibles
                bajo solicitud a{" "}
                <a
                  href="mailto:support@keepsoul.app"
                  className="text-gold hover:underline"
                >
                  support@keepsoul.app
                </a>
                ; sede en Murcia, España; en adelante, "KeepSoul" o "el
                Prestador") consistente en una plataforma para guardar de
                forma cifrada información sensible, mensajes y contenidos del
                usuario, así como en entregarlos a herederos designados tras
                la confirmación documental de su fallecimiento. El servicio
                incluye, entre otras, las siguientes funcionalidades:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>El Cofre</strong> — almacenamiento cifrado de
                  contraseñas, semillas de criptomonedas, IBAN, documentos y
                  notas sensibles.
                </li>
                <li>
                  <strong>Mensajes del Futuro</strong> — programación de
                  mensajes (texto, audio, vídeo, imagen) para entrega diferida
                  en fechas o eventos predefinidos.
                </li>
                <li>
                  <strong>Herencia Digital</strong> — designación de herederos
                  con porcentajes de asignación del legado y mensajes
                  personalizados.
                </li>
                <li>
                  <strong>Fe de Vida</strong> — protocolo de verificación
                  periódica del estado del usuario y activación de la entrega
                  del legado.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                2. Aceptación
              </h2>
              <p>
                La creación de una cuenta y el uso del servicio implican la
                aceptación plena y sin reservas de los presentes Términos y
                Condiciones, de la{" "}
                <Link
                  to="/legal/privacidad"
                  className="text-gold hover:underline"
                >
                  Política de Privacidad
                </Link>{" "}
                y de la{" "}
                <Link
                  to="/legal/cookies"
                  className="text-gold hover:underline"
                >
                  Política de Cookies
                </Link>
                . Si no estás de acuerdo, por favor no uses el servicio.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                3. Planes y precios
              </h2>
              <ul className="list-disc pl-6 space-y-1 mb-3">
                <li>
                  <strong>Gratuito:</strong> acceso básico con límites de
                  almacenamiento y funciones.
                </li>
                <li>
                  <strong>Personal:</strong> suscripción mensual con todas las
                  funciones.
                </li>
                <li>
                  <strong>De por Vida:</strong> pago único de 149€ con acceso
                  permanente a todas las funciones mientras KeepSoul
                  permanezca operativo.
                </li>
              </ul>
              <p className="mt-3 text-xs text-faded">
                Todos los pagos se procesan en EUR a través de Stripe.
                Los precios actualizados están publicados en la página de
                Planes dentro de la app.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                4. Derecho de desistimiento
              </h2>
              <p className="mb-3">
                De acuerdo con el TRLGDCU (Real Decreto Legislativo 1/2007), el
                usuario consumidor tiene derecho a desistir del contrato en un
                plazo de <strong>14 días naturales</strong> desde su
                celebración, sin necesidad de justificación y sin penalización.
                Para ejercer este derecho, basta con remitir un email a{" "}
                <a
                  href="mailto:support@keepsoul.app"
                  className="text-gold hover:underline"
                >
                  support@keepsoul.app
                </a>{" "}
                desde la dirección registrada.
              </p>
              <p className="text-faded">
                <strong>Excepción:</strong> conforme al art. 103.m TRLGDCU, el
                derecho de desistimiento{" "}
                <strong>
                  no es aplicable a contenidos digitales que se hayan empezado
                  a ejecutar
                </strong>{" "}
                con el consentimiento previo y expreso del consumidor (por
                ejemplo, si el usuario ha subido contenido al Cofre, ha
                programado mensajes futuros o ha designado herederos). El
                usuario reconoce y acepta esta excepción al iniciar el uso
                activo de las funcionalidades de pago.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                5. Plan "De por Vida" — continuidad del servicio
              </h2>
              <p className="mb-3">
                El plan De por Vida es un pago único que otorga acceso a todas
                las funcionalidades de KeepSoul mientras el Prestador continúe
                operando el servicio. En caso de cese de actividad:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  KeepSoul notificará el cierre por email a todos los usuarios
                  con al menos <strong>90 días naturales de antelación</strong>.
                </li>
                <li>
                  Durante ese plazo, se habilitará la exportación completa de
                  los datos del usuario en formato estructurado.
                </li>
                <li>
                  En la medida de lo razonablemente posible, KeepSoul facilitará
                  la portabilidad a un servicio equivalente sin coste
                  adicional para el usuario.
                </li>
              </ul>
              <p className="mt-3 text-faded">
                El plan De por Vida no constituye una garantía perpetua del
                servicio. La responsabilidad económica de KeepSoul ante el
                cese se limita a lo establecido en la cláusula 8.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                6. Obligaciones del usuario
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Proporcionar datos veraces y mantenerlos actualizados,
                  especialmente los relativos a los herederos y contactos de
                  confianza.
                </li>
                <li>
                  No utilizar el servicio para almacenar contenidos ilícitos
                  ni para actividades contrarias al ordenamiento jurídico.
                </li>
                <li>
                  Custodiar de forma segura la contraseña maestra y los
                  códigos de acceso. El usuario es responsable de cualquier
                  uso de su cuenta.
                </li>
                <li>
                  Verificar periódicamente la configuración de Fe de Vida y los
                  datos de contacto de los herederos.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                7. Sistema Fe de Vida y entrega del legado
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Periodicidad:</strong> el usuario configura el
                  intervalo de Fe de Vida desde Ajustes (por defecto, 90 días).
                </li>
                <li>
                  <strong>Procedimiento de no respuesta:</strong> si el usuario
                  no confirma su Fe de Vida en el plazo, KeepSoul activa un
                  escalado de notificaciones (email al usuario, contactos de
                  confianza y, posteriormente, herederos).
                </li>
                <li>
                  <strong>Activación del legado:</strong> una vez verificado
                  documentalmente el fallecimiento, KeepSoul entrega el legado
                  a los herederos en los porcentajes y condiciones definidos
                  por el usuario.
                </li>
                <li>
                  <strong>Documentación requerida al heredero:</strong>{" "}
                  certificado de defunción oficial, copia del DNI/NIE u
                  documento identificativo equivalente y cualquier otra
                  documentación razonablemente exigible para acreditar la
                  legitimación.
                </li>
                <li>
                  KeepSoul podrá denegar la entrega del legado si la
                  documentación aportada es insuficiente o presenta indicios
                  razonables de fraude.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                8. Limitación de responsabilidad
              </h2>
              <p className="mb-3">
                KeepSoul cifra los datos sensibles mediante AES-256-GCM en
                reposo y aplica medidas técnicas y organizativas razonables
                para garantizar la seguridad e integridad del servicio. No
                obstante:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  KeepSoul no se hace responsable de daños indirectos,
                  consecuenciales o derivados del lucro cesante.
                </li>
                <li>
                  La pérdida de la contraseña maestra puede impedir el acceso
                  a los datos del Cofre. El usuario es el único custodio de
                  sus credenciales.
                </li>
                <li>
                  La <strong>responsabilidad máxima</strong> de KeepSoul ante
                  el usuario por cualquier reclamación queda limitada al{" "}
                  <strong>
                    importe total efectivamente pagado por el usuario en los
                    últimos 12 meses
                  </strong>
                  .
                </li>
                <li>
                  Esta limitación no resulta de aplicación en los casos de
                  dolo, negligencia grave imputable a KeepSoul, ni en aquellos
                  supuestos en que la legislación de consumo imperativa lo
                  prohíba.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                9. Propiedad intelectual
              </h2>
              <p>
                El contenido subido por el usuario (textos, archivos,
                mensajes, imágenes, audios y vídeos) es y seguirá siendo de su
                titularidad. KeepSoul únicamente lo almacena cifrado a los
                exclusivos efectos de prestar el servicio. La marca, el
                software, el diseño y los demás elementos del Sitio son
                propiedad del Prestador y están protegidos por la legislación
                aplicable.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                10. Modificación de los Términos
              </h2>
              <p>
                Cualquier modificación material de los presentes Términos
                será notificada por email a los usuarios registrados con al
                menos <strong>30 días naturales de antelación</strong>.
                Transcurrido ese plazo, el uso continuado del servicio
                implicará la aceptación de la nueva versión. Si no estás de
                acuerdo, puedes resolver el contrato y eliminar tu cuenta
                antes de la entrada en vigor.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                11. Resolución del contrato
              </h2>
              <p>
                El usuario puede cancelar el servicio y eliminar su cuenta en
                cualquier momento desde Ajustes. KeepSoul podrá suspender o
                resolver el contrato en caso de incumplimiento grave de los
                presentes Términos por parte del usuario, especialmente si se
                detecta uso ilícito del servicio.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                12. Ley aplicable y jurisdicción
              </h2>
              <p>
                Los presentes Términos se rigen por la legislación española.
                Las controversias derivadas del contrato se someten a los{" "}
                <strong>Juzgados y Tribunales de Murcia</strong>, salvo cuando
                el usuario tenga la condición de consumidor, en cuyo caso se
                aplicará el fuero que le corresponda según el TRLGDCU.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                13. Contacto
              </h2>
              <p>
                Para cualquier consulta relacionada con estos Términos:{" "}
                <a
                  href="mailto:support@keepsoul.app"
                  className="text-gold hover:underline"
                >
                  support@keepsoul.app
                </a>
                .
              </p>
            </section>
          </div>
        )}

        <div className="mt-12 pt-6 border-t border-gold-subtle text-xs text-faded text-center space-x-3">
          <Link to="/legal/aviso-legal" className="hover:text-gold">
            Aviso Legal
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/legal/privacidad" className="hover:text-gold">
            Privacidad
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/legal/cookies" className="hover:text-gold">
            Cookies
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/" className="hover:text-gold">
            Inicio
          </Link>
        </div>
      </main>
    </div>
  );
}

function LegalLangNotice({ lang }) {
  if (lang === "zh") {
    return (
      <div className="rounded border border-gold-subtle bg-obsidian-2/40 p-5 text-sm leading-relaxed">
        <p className="mb-2 text-gold-light">
          法律文件目前以西班牙语提供,西班牙语是依据西班牙消费者法律管辖本合同的语言。
        </p>
        <p className="text-faded">
          英文和中文译本即将提供。如需说明,请联系:{" "}
          <a
            href="mailto:support@keepsoul.app"
            className="text-gold hover:underline"
          >
            support@keepsoul.app
          </a>
        </p>
      </div>
    );
  }
  return (
    <div className="rounded border border-gold-subtle bg-obsidian-2/40 p-5 text-sm leading-relaxed">
      <p className="mb-2 text-gold-light">
        The legal documentation is currently available in Spanish, which is the
        language governing the contract under Spanish consumer law.
      </p>
      <p className="text-faded">
        An English translation will be available soon. For clarifications:{" "}
        <a
          href="mailto:support@keepsoul.app"
          className="text-gold hover:underline"
        >
          support@keepsoul.app
        </a>
      </p>
    </div>
  );
}
